import { FC, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { t } from 'i18next';
import ETextInput from '@/components/everridge/ETextInput';
import ETextAreaInput from '@/components/everridge/ETextAreaInput';
import { useFaqDetailsApi } from '@/features/faq/hooks/useFaqDetailsApi';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	FaqManageFormData,
	FaqManageFormSchema,
} from '@/features/faq/validators/FaqManageValidator';
import { CreateFaqDto, UpdateFaqDto } from '@/features/faq/types/faq-dto';
import EFaqIconsInput from '@/components/everridge/EFaqIconsInput';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';

type Props = {
	id?: string;
	onClose: () => void;
};

const FaqManage: FC<Props> = ({ id, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { faqDetailsData, createFaq, updateFaq } = useFaqDetailsApi({ id });

	const { control, handleSubmit, reset } = useForm<FaqManageFormData>({
		resolver: zodResolver(FaqManageFormSchema),
	});

	useEffect(() => {
		if (faqDetailsData) {
			reset({
				iconName: faqDetailsData.iconName,
				question: faqDetailsData.question,
				answer: faqDetailsData.answer,
				linkUrl: faqDetailsData.linkUrl,
			});
		}
	}, [faqDetailsData, reset]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleCreateForm = async (form: FaqManageFormData) => {
		const dto: CreateFaqDto = {
			iconName: form.iconName,
			question: form.question,
			answer: form.answer,
			linkUrl: form.linkUrl,
		};

		await createFaq(dto);
		handleClose();
	};

	const handleUpdateForm = async (form: FaqManageFormData) => {
		const dto: UpdateFaqDto = {
			iconName: form.iconName,
			question: form.question,
			answer: form.answer,
			linkUrl: form.linkUrl,
		};

		await updateFaq(dto);
		handleClose();
	};

	const postButtons = (
		<>
			<Button
				variant='outline'
				onClick={handleClose}>
				{t('elements.buttons.cancel')}
			</Button>
			<Button onClick={handleSubmit(handleCreateForm)}>
				{t('elements.buttons.createFAQ')}
			</Button>
		</>
	);

	const editButtons = (
		<>
			<Button
				variant='outline'
				onClick={handleClose}>
				{t('elements.buttons.cancel')}
			</Button>
			<Button onClick={handleSubmit(handleUpdateForm)}>
				{t('elements.buttons.saveChanges')}
			</Button>
		</>
	);

	return (
		<>
			<Dialog
				open={isOpen}
				onOpenChange={handleClose}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{!!id
								? t('main.faq.components.manageFAQ.title2')
								: t('main.faq.components.manageFAQ.title1')}
							<DialogClose
								className={cn(
									'ml-auto cursor-pointer',
									isMobile &&
										'h-11 p-3 border border-popover-100 rounded-full',
								)}>
								<Cancel_01_Icon />
							</DialogClose>
						</DialogTitle>
						<DialogDescription />
					</DialogHeader>
					<div className='flex flex-col gap-4'>
						<EFaqIconsInput
							control={control}
							fieldName={'iconName'}
							label={t('elements.form.icon.label')}
							placeholder={t('elements.form.icon.placeholder')}
						/>
						<ETextInput
							control={control}
							fieldName={'question'}
							label={t('elements.form.question.label')}
							placeholder={t(
								'elements.form.question.placeholder',
							)}
						/>
						<ETextAreaInput
							control={control}
							fieldName={'answer'}
							label={t('elements.form.answer.label')}
							placeholder={t('elements.form.answer.placeholder')}
						/>
						<ETextInput
							control={control}
							fieldName={'linkUrl'}
							label={t('elements.form.link.label')}
							placeholder={t('elements.form.link.placeholder')}
							optional={true}
						/>
					</div>
					<DialogFooter
						className={cn('justify-end', isMobile && 'mt-auto')}>
						{!!id ? editButtons : postButtons}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default FaqManage;
